import { fetchMarketplaces } from '../api/storeMarketplaces';
import { getBaseDomain, hostnamesMatch } from './registrableDomain';

export {
  getBaseDomain,
  hostnamesMatch,
  matchMarketplaceDefinitionForUrl,
  normalizeHostname,
  toRegistrableDomain,
} from './registrableDomain';

/**
 * Resolve marketplace platform identifier and logo URL from a listing URL
 */
export const resolveMarketplace = async (
  listingUrl: string
): Promise<{
  platform?: string;
  logoUrl?: string;
  marketplaceName?: string;
  country?: string;
}> => {
  try {
    const marketplaces = await fetchMarketplaces();
    const url = new URL(listingUrl);
    const listingHostname = url.hostname.toLowerCase();
    const listingBaseDomain = getBaseDomain(listingHostname);

    const matchingMarketplace = marketplaces
      .map(marketplace => {
        if (!marketplace.url) return null;
        try {
          const marketplaceUrl = new URL(marketplace.url);
          const marketplaceHostname = marketplaceUrl.hostname.toLowerCase();
          const marketplaceBaseDomain = getBaseDomain(marketplaceHostname);
          let score = 0;
          if (hostnamesMatch(listingHostname, marketplaceHostname)) {
            score = 3;
          } else if (listingBaseDomain === marketplaceBaseDomain) {
            score = 2;
          } else {
            return null;
          }
          return { marketplace, score, specificity: marketplaceBaseDomain.length };
        } catch {
          return null;
        }
      })
      .filter((row): row is NonNullable<typeof row> => row !== null)
      .sort((a, b) => b.score - a.score || b.specificity - a.specificity)[0]?.marketplace;

    if (matchingMarketplace) {
      return {
        platform: matchingMarketplace.identifier,
        logoUrl: matchingMarketplace.logo,
        marketplaceName: matchingMarketplace.name,
        country: matchingMarketplace.country,
      };
    }

    return {};
  } catch (error) {
    console.error('Error resolving marketplace:', error);
    return {};
  }
};

/**
 * Get marketplace logo URL for a listing URL
 */
export const getMarketplaceLogo = async (listingUrl: string): Promise<string | undefined> => {
  const { logoUrl } = await resolveMarketplace(listingUrl);
  return logoUrl;
};

/**
 * Get marketplace platform identifier for a listing URL
 */
export const getMarketplacePlatform = async (listingUrl: string): Promise<string | undefined> => {
  const { platform } = await resolveMarketplace(listingUrl);
  return platform;
};

/**
 * Apply post-processing from a field definition to extract a value from a URL.
 * Mirrors the backend's apply_post_processing for regex_replace and trim_query_params.
 */
function applyPostProcessing(
  url: string,
  postProcessing: { type?: string; pattern?: string; replacement?: string } | undefined
): string {
  if (!postProcessing || !url) return url;
  const t = postProcessing.type;
  if (t === 'regex_replace') {
    const pattern = postProcessing.pattern;
    let replacement = postProcessing.replacement ?? '';
    // Convert Python-style backreferences (\1, \2) to JS ($1, $2)
    replacement = String(replacement).replace(/\\([1-9]\d*)/g, (_, n) => '$' + n);
    if (pattern) {
      try {
        const result = url.replace(new RegExp(pattern), replacement);
        return result != null ? String(result) : url;
      } catch {
        return url;
      }
    }
  }
  if (t === 'trim_query_params') {
    const idx = url.indexOf('?');
    return idx >= 0 ? url.slice(0, idx) : url;
  }
  return url;
}

/**
 * Extract seller identifier from a seller URL using the marketplace definition's
 * field extraction rules (source: 'url' with post_processing). Looks at seller_page,
 * seller_profile, and product_listing fields to find URL-based identifier extraction.
 *
 * @param sellerUrl - The seller's store/profile URL
 * @param definition - Marketplace definition (from useMarketplaceDefinitions) or null
 * @returns Extracted identifier string, or null if definition has no applicable rule
 */
type MarketplaceDefinitionForExtraction =
  | {
      config?: {
        pages?: Record<
          string,
          {
            fields?: Record<
              string,
              {
                source?: string;
                post_processing?: { type?: string; pattern?: string; replacement?: string };
              }
            >;
          }
        >;
      };
    }
  | null
  | undefined;

export function extractSellerIdentifierFromDefinition(
  sellerUrl: string | undefined,
  definition: MarketplaceDefinitionForExtraction
): string | null {
  if (!sellerUrl || typeof sellerUrl !== 'string' || !definition?.config?.pages) return null;
  const pages = definition.config.pages;
  const pageKeys = ['seller_page', 'seller_profile', 'product_listing'] as const;
  for (const key of pageKeys) {
    const page = pages[key];
    const fields = page?.fields;
    if (!fields || typeof fields !== 'object') continue;
    for (const [, fieldConfig] of Object.entries(fields)) {
      const fc = fieldConfig as {
        source?: string;
        post_processing?: { type?: string; pattern?: string; replacement?: string };
      };
      if (fc?.source !== 'url' || !fc?.post_processing) continue;
      const result = applyPostProcessing(sellerUrl, fc.post_processing);
      const trimmed = typeof result === 'string' ? result.trim() : '';
      // Only treat as identifier if non-empty and not the full URL (extraction occurred)
      if (trimmed && trimmed !== sellerUrl && trimmed.length < sellerUrl.length) {
        return trimmed;
      }
    }
  }
  return null;
}
